import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Tag,
  Popconfirm,
  Select,
  Row,
  Col,
  Card,
  Empty,
  Tooltip,
  Checkbox,
  Tabs,
  Statistic,
  Badge,
  Drawer,
  Steps,
  Typography,
  Divider
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditFilled,
  DeleteFilled,
  EyeOutlined,
  EyeInvisibleOutlined,
  DatabaseOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { logsService } from "../services/logsApi";
import { useAuth } from "../context/AuthContext";
import { accountService } from "../services/accountService";
import { databricksService } from "../services/databricksService";
import { useTheme } from "../context/ThemeContext";
import IsEnabled from "../rolepermission";

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

// CloudWatch onboarding currently supported only in us-east-1.
// Keep other regions visible but disabled to avoid confusion.
const CLOUDWATCH_REGION_OPTIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)", disabled: false },
  { value: "us-east-2", label: "US East (Ohio)", disabled: true },
  { value: "us-west-1", label: "US West (N. California)", disabled: true },
  { value: "us-west-2", label: "US West (Oregon)", disabled: true },
  { value: "eu-west-1", label: "EU (Ireland)", disabled: true },
  { value: "eu-west-2", label: "EU (London)", disabled: true },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)", disabled: true },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)", disabled: true },
];

const showLogOnboardError = (error, actionLabel = "creating logs") => {
  const msg = error?.message || "Failed to onboard logs";
  if (error?.status === 409) {
    message.warning(msg);
    return;
  }
  message.error(`Error ${actionLabel}: ${msg}`);
};

const normalizeLogSourceType = (source) => {
  const raw = String(source || "").trim().toLowerCase();
  if (raw === "cloudwatch" || raw === "cloud watch") return "CloudWatch";
  if (raw === "s3" || raw === "s3 logs" || raw.startsWith("s3 ")) return "s3";
  return String(source || "").trim();
};

const isCloudWatchLogSource = (source) =>
  normalizeLogSourceType(source) === "CloudWatch";

const isS3LogSource = (source) => normalizeLogSourceType(source) === "s3";

const isServiceTypeSearchQuery = (query) => {
  const normalized = String(query || "").trim().toLowerCase();
  if (!normalized) return false;
  if (
    normalized === "s3" ||
    normalized === "s3 logs" ||
    normalized.startsWith("s3://")
  ) {
    return true;
  }
  if (normalized === "cloudwatch" || normalized === "cloudwatch logs") {
    return true;
  }
  // Prefix match only (avoid single letters like "d" matching "cloudwatch")
  if (normalized.length >= 3 && "cloudwatch".startsWith(normalized)) {
    return true;
  }
  return false;
};

const logSourceTypeMatchesSearch = (source, searchLower) => {
  if (!searchLower) return false;
  const serviceLabel = normalizeLogSourceType(source).toLowerCase();
  if (
    searchLower === "s3" ||
    searchLower === "s3 logs" ||
    searchLower.startsWith("s3://") ||
    (searchLower.length >= 2 && "s3".startsWith(searchLower))
  ) {
    return serviceLabel === "s3";
  }
  if (
    searchLower === "cloudwatch" ||
    searchLower === "cloudwatch logs" ||
    (searchLower.length >= 3 && "cloudwatch".startsWith(searchLower))
  ) {
    return serviceLabel === "cloudwatch";
  }
  return false;
};

const LogsPage = () => {
  const { user, accounts } = useAuth();
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingLog, setViewingLog] = useState(null);
  const [form] = Form.useForm();
  const [editingLog, setEditingLog] = useState(null);
  const [filterService, setFilterService] = useState("All Services");
  const [logEntries, setLogEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState('aws'); // 'aws' or 'databricks'
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [currentArnInput, setCurrentArnInput] = useState("");
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [databricksModalVisible, setDatabricksModalVisible] = useState(false);
  const [databricksForm] = Form.useForm();
  const [databricksWorkspaces, setDatabricksWorkspaces] = useState([]);
  const [databricksClusterLogPaths, setDatabricksClusterLogPaths] = useState([]);
  const [selectedDatabricksAccount, setSelectedDatabricksAccount] = useState(null);
  const [awsAccountsForDatabricks, setAwsAccountsForDatabricks] = useState([]);
  const [activeTab, setActiveTab] = useState('aws');
  const [searchQuery, setSearchQuery] = useState('');
  const [pipelineLogPaths, setPipelineLogPaths] = useState([]);
  const [strictLogFilter, setStrictLogFilter] = useState(false);
  const [pipelineFilterName, setPipelineFilterName] = useState('');
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedSourceDetail, setSelectedSourceDetail] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);
  const isViewer = !IsEnabled('Logs');
  const highlightSearchText = useCallback((value) => {
    const text = String(value ?? "");
    const query = searchQuery.trim();
    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let cursor = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > cursor) {
        parts.push(text.slice(cursor, matchIndex));
      }
      const match = text.slice(matchIndex, matchIndex + query.length);
      parts.push(
        <mark
          key={`${matchIndex}-${match}`}
          style={{
            padding: "0 2px",
            borderRadius: 3,
            background: isDark ? "#5c4300" : "#fff1b8",
            color: isDark ? "#fff7e6" : "inherit",
          }}
        >
          {match}
        </mark>
      );
      cursor = matchIndex + query.length;
      matchIndex = lowerText.indexOf(lowerQuery, cursor);
    }

    if (cursor < text.length) {
      parts.push(text.slice(cursor));
    }

    return parts.length ? parts : text;
  }, [isDark, searchQuery]);

  // Apply pipeline/logPath filter from URL (?pipeline=name, ?logPath=path) - from Pipeline Discovery "View Logs"
  useEffect(() => {
    const pipeline = searchParams.get('pipeline');
    const logPath = searchParams.get('logPath');
    const logPathsParam = searchParams.get('logPaths');
    const strict = searchParams.get('strict') === '1';
    const accountIdParam = searchParams.get('accountId');

    let paths = [];
    if (strict) {
      if (logPathsParam) {
        paths = logPathsParam.split('|').map(decodeURIComponent).filter(Boolean);
      } else if (logPath?.trim()) {
        paths = [logPath.trim()];
      }
    } else if (logPathsParam) {
      paths = logPathsParam.split('|').map(decodeURIComponent).filter(Boolean);
    } else if (logPath?.trim()) {
      paths = [logPath.trim()];
    }

    setPipelineLogPaths(paths);
    setStrictLogFilter(strict);
    setPipelineFilterName(pipeline?.trim() || '');

    if (paths.length) {
      const bestPath = [...paths].sort(
        (a, b) => String(b).length - String(a).length,
      )[0];
      setSearchQuery(bestPath);
    } else if (pipeline?.trim()) {
      setSearchQuery(pipeline.trim());
    }

    if (accountIdParam) {
      setIsAccountLocked(true);
      setSelectedAccount(accountIdParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const accountIdParam = searchParams.get('accountId');
    if (!accountIdParam || accountOptions.length === 0) return;
    const accountFromUrl = accountOptions.find((opt) => opt.value === accountIdParam);
    if (!accountFromUrl) return;
    setSelectedAccount(accountIdParam);
    setSelectedAccountType(accountFromUrl.type);
    setActiveTab(accountFromUrl.type);
    setIsAccountLocked(true);
    if (accountFromUrl.type === 'databricks') {
      const dbAccount = databricksAccounts.find(
        (acc) => acc.accountId === accountFromUrl.accountId,
      );
      setSelectedDatabricksAccount(dbAccount);
    }
  }, [searchParams, accountOptions, databricksAccounts]);

  const parseArn = (arn) => {
    if (!arn || typeof arn !== "string")
      return { region: "N/A", log_group: "N/A", bucket: null, path: null };
    
    // Handle S3 ARNs
    if (arn.startsWith("s3://")) {
      const s3Parts = arn.substring(5).split('/', 2);
      const bucket = s3Parts[0] || "N/A";
      const path = s3Parts.length > 1 ? `/${arn.substring(5 + bucket.length + 1)}` : "";
      // For S3, we'll always return "Global" as the region
      return { region: "Global", log_group: "N/A", bucket, path };
    }
    
    // Handle CloudWatch log group names (e.g., /aws/lambda/my-function)
    // If it starts with /, it's likely a log group name, not an ARN
    if (arn.startsWith("/")) {
      return { region: "N/A", log_group: arn, bucket: null, path: null };
    }
    
    // Handle CloudWatch ARNs (arn:aws:logs:region:account:log-group:log-group-name)
    const parts = arn.split(":");
    if (parts.length >= 6 && parts[0] === "arn" && parts[1] === "aws" && parts[2] === "logs") {
    const region = parts[3] || "N/A";
    const log_group =
      parts[5] === "log-group" && parts[6]
        ? parts[6]
        : parts.slice(5).join(":") || "N/A";
    return { region, log_group, bucket: null, path: null };
    }
    
    // Fallback: treat as log group name if it doesn't match ARN format
    return { region: "N/A", log_group: arn, bucket: null, path: null };
  };

  // Convert log group name to full ARN when sending to backend (backend expects ARN format)
  const toCloudWatchArn = (input, accountId, region) => {
    if (!input || !accountId || !region) return input;
    if (input.startsWith("s3://")) return input;
    if (input.startsWith("arn:aws:logs:")) return input;
    return `arn:aws:logs:${region}:${accountId}:log-group:${input}`;
  };

  const validateArn = (arn, accountId, service) => {
  if (!arn) {
    return "Please enter log string";
  }

  const trimmed = arn.trim();
  const acctDigits = accountId ? String(accountId).replace(/^aws_/, "") : "";
  const serviceKey = String(service || "").trim().toLowerCase();

  if (serviceKey === "cloudwatch") {
    if (trimmed.startsWith("s3://") || /^s3:/i.test(trimmed)) {
      return "S3 URIs belong under S3 Logs, not CloudWatch. Switch the service to S3 Logs.";
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return "Invalid CloudWatch log group format";
    }
    const isLogGroupName = trimmed.startsWith("/");
    const isCloudWatchArn = trimmed.startsWith("arn:aws:logs:");
    if (!isLogGroupName && !isCloudWatchArn) {
      return "Enter a CloudWatch log group name (e.g. /aws/lambda/my-function) or ARN";
    }
    if (
      isCloudWatchArn &&
      (!acctDigits || !trimmed.includes(acctDigits))
    ) {
      return "Log group doesn't belong to this account";
    }
  }

  if (serviceKey === "s3") {
    if (trimmed.startsWith("/aws/") || trimmed.startsWith("arn:aws:logs:")) {
      return "CloudWatch log groups belong under CloudWatch Logs, not S3. Switch the service to CloudWatch Logs.";
    }
    if (!trimmed.startsWith("s3://")) {
      return "Invalid S3 URI. Use format s3://bucket-name/prefix/";
    }
    const { bucket } = parseArn(trimmed);
    if (!bucket) {
      return "Invalid S3 URI: Bucket not found";
    }
    if (bucket === acctDigits) {
      return "Invalid S3 Bucket name";
    }
  }

  return null;
};



  // Helper function to get stored credentials from localStorage
  const getStoredCredentials = (accountId) => {
    try {
      const stored = localStorage.getItem(`databricks_credentials_${accountId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  };

  const storeCredentials = (accountId, credentials) => {
    try {
      localStorage.setItem(`databricks_credentials_${accountId}`, JSON.stringify({
        client_id: credentials.client_id,
        client_secret: credentials.client_secret,
        pat_token: credentials.pat_token,
        account_url: credentials.account_url,
      }));
    } catch (error) {
      console.error('Error storing in localStorage:', error);
    }
  };
//
  const fetchAccounts = useCallback(async () => {
    if (!user?.client_id) return;
    try {
      // Fetch AWS accounts
      const allAccounts = await accountService.getAccounts();
      const awsOptions = [];
      
      // Extract AWS accounts from all clients
      for (const clientId in allAccounts) {
        Object.entries(allAccounts[clientId] || {}).forEach(([accountId, details]) => {
          awsOptions.push({
            value: `aws_${accountId}`,
            label: `${details.aws_account_name} (${accountId})`,
            type: 'aws',
            accountId: accountId,
            clientId: clientId,
          });
        });
      }

      // Fetch Databricks accounts
      let databricksOptions = [];
      try {
        const databricksAccountsList = await databricksService.getAccounts();
        const accountsWithDetails = databricksAccountsList.map((account) => {
          const accountId = account.account_id;
          const accountName = account.account_name || `Databricks Account ${accountId}`;
          const accountUrl = account.account_url || 'https://accounts.cloud.databricks.com';

          return {
            value: `databricks_${accountId}`,
            label: accountName,  // Display account name instead of account ID
            type: 'databricks',
            accountId: accountId,
            accountName: accountName,
            clientId: account.client_id,
            accountUrl: accountUrl,
          };
        });
        databricksOptions = accountsWithDetails;
        setDatabricksAccounts(accountsWithDetails);
      } catch (error) {
        console.error("Error fetching Databricks accounts:", error);
      }

      const allOptions = [...awsOptions, ...databricksOptions];
      setAccountOptions(allOptions);
      
      // Only set default account on initial load - use functional update to read current state
      setSelectedAccount((prev) => {
        if (prev) return prev; // Keep current selection when refreshing accounts
        if (allOptions.length === 0) return null;
        return allOptions[0].value;
      });
      setSelectedAccountType((prev) => {
        if (prev) return prev;
        if (allOptions.length === 0) return 'aws';
        return allOptions[0].type;
      });
      setActiveTab((prev) => {
        if (prev) return prev;
        if (allOptions.length === 0) return 'aws';
        return allOptions[0].type;
      });
      
      // Check for accountId in URL query parameters (only applies when no selection yet)
      const urlParams = new URLSearchParams(window.location.search);
      const accountIdParam = urlParams.get('accountId');
      if (accountIdParam && allOptions.length > 0) {
        const accountFromUrl = allOptions.find(opt => opt.value === accountIdParam);
        if (accountFromUrl) {
          setSelectedAccount(accountFromUrl.value);
          setSelectedAccountType(accountFromUrl.type);
          setActiveTab(accountFromUrl.type);
          if (accountFromUrl.type === 'databricks') {
            const dbAccount = databricksOptions.find(acc => acc.accountId === accountFromUrl.accountId);
            setSelectedDatabricksAccount(dbAccount);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
      message.error("Failed to fetch accounts");
    }
  }, [user]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchLogs = useCallback(
    async (accountValue) => {
      if (!accountValue || !user?.client_id) {
        setAllLogs([]);
        return;
      }
      setLoading(true);
      try {
        if (accountValue.startsWith('aws_')) {
          // Fetch AWS logs
          const awsAccountId = accountValue.replace('aws_', '');
          const dataArray = await logsService.getOnboardedLogs(
            awsAccountId,
            user.client_id
          );
          
          const groupedBySource = {};
          dataArray.forEach((item) => {
            (item.log_selector || []).forEach((selector) => {
              const normalizedSource = normalizeLogSourceType(selector.source);
              const key = `${item.aws_account_id}|${normalizedSource}`;
              if (!groupedBySource[key]) {
                groupedBySource[key] = {
                  id: key,
                  client_id: item.client_id,
                  AccountId: item.aws_account_id,
                  source: normalizedSource,
                  logsSelected: [],
                  accountType: 'aws',
                };
              }
              if (!groupedBySource[key].logsSelected.includes(selector.arn)) {
                groupedBySource[key].logsSelected.push(selector.arn);
              }
            });
          });
          const transformedData = Object.values(groupedBySource);
          setAllLogs(transformedData);
        } else if (accountValue.startsWith('databricks_')) {
          // Extract the selected account ID from the account value
          const selectedAccountId = accountValue.replace('databricks_', '');
          
          // Fetch ALL Databricks logs using the new unified endpoint
          try {
            const allLogsResponse = await logsService.getAllDatabricksLogs();
            const allDatabricksLogs = [];
            
            if (allLogsResponse && allLogsResponse.workspaces) {
              for (const workspace of allLogsResponse.workspaces) {
                // Filter logs for only the selected account
                if (workspace.account_id !== selectedAccountId) {
                  continue;
                }
                
                const logPaths = [];
                const logTypes = [];
                
                if (workspace.cluster_log_path) {
                  logPaths.push(workspace.cluster_log_path);
                  logTypes.push('Cluster Logs');
                }
                
                if (workspace.audit_log_path) {
                  logPaths.push(workspace.audit_log_path);
                  logTypes.push('Audit Logs');
                }

                if (logPaths.length > 0) {
                  // Find account name from databricksAccounts
                  const dbAccount = databricksAccounts.find(acc => acc.accountId === workspace.account_id);
                  const accountName = dbAccount?.accountName || `Databricks Account ${workspace.account_id}`;
                  
                  allDatabricksLogs.push({
                    id: `databricks_${workspace.account_id}_${workspace.workspace_name}`,
                    client_id: user.client_id,
                    AccountId: workspace.account_id,
                    accountName: accountName,  // Include account name for display
                    source: 's3',
                    logsSelected: logPaths,
                    logTypes: logTypes,
                    accountType: 'databricks',
                    workspaceName: workspace.workspace_name,
                    awsAccountId: workspace.aws_account_id,
                    enabled: workspace.enabled,
                    updatedAt: workspace.updated_at,
                  });
                }
              }
            }
            setAllLogs(allDatabricksLogs);
          } catch (error) {
            console.error("Error fetching Databricks logs:", error);
            setAllLogs([]);
          }
        }
      } catch (error) {
        message.error("Error fetching logs: " + error.message);
        setAllLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [user, databricksAccounts]
  );

  useEffect(() => {
    if (selectedAccount) {
      fetchLogs(selectedAccount);
    } else {
      setAllLogs([]);
    }
  }, [selectedAccount, fetchLogs]);

  const extractLogGroupKey = (pathOrArn) => {
    if (!pathOrArn) return "";
    const raw = String(pathOrArn).trim();
    if (raw.startsWith("arn:aws:logs:")) {
      const parts = raw.split(":");
      if (parts.length >= 7) {
        let group = parts[6];
        if (group.startsWith("log-group:")) {
          group = group.slice("log-group:".length);
        }
        return group.split(":*")[0].toLowerCase();
      }
    }
    if (raw.startsWith("/")) {
      return raw.split(":*")[0].toLowerCase();
    }
    if (raw.startsWith("s3://")) {
      return raw.toLowerCase();
    }
    return raw.toLowerCase();
  };

  const logArnMatchesFilterPath = (arn, filterPath, strict = false) => {
    if (!arn || !filterPath) return false;
    if (strict) {
      const arnKey = extractLogGroupKey(arn);
      const filterKey = extractLogGroupKey(filterPath);
      if (arnKey && filterKey) {
        return arnKey === filterKey;
      }
      return String(arn).toLowerCase() === String(filterPath).toLowerCase();
    }
    const arnLower = String(arn).toLowerCase();
    const pathLower = String(filterPath).toLowerCase();
    if (arnLower === pathLower || arnLower.includes(pathLower) || pathLower.includes(arnLower)) {
      return true;
    }
    const { log_group: logGroup, bucket, path } = parseArn(arn);
    const candidates = [logGroup, bucket, path].filter(Boolean).map((v) => String(v).toLowerCase());
    return candidates.some(
      (candidate) =>
        candidate === pathLower ||
        candidate.includes(pathLower) ||
        pathLower.includes(candidate),
    );
  };

  const logArnMatchesPipelineName = (arn, pipelineName) => {
    if (!arn || !pipelineName) return false;
    const hay = String(arn).toLowerCase();
    const name = String(pipelineName).toLowerCase();
    return (
      hay.includes(`/${name}`) ||
      hay.includes(`_${name}`) ||
      hay.endsWith(name) ||
      hay.includes(`:${name}`)
    );
  };

  const logEntryMatchesSearch = useCallback((arn, searchLower) => {
    if (!arn || !searchLower) return false;
    if (String(arn).toLowerCase().includes(searchLower)) return true;
    const { log_group, bucket, path } = parseArn(arn);
    return [log_group, bucket, path].some(
      (value) => value && String(value).toLowerCase().includes(searchLower),
    );
  }, []);

  const logSourceMatchesSearch = useCallback(
    (log, searchLower) => {
      if (!searchLower) return true;
      if (isServiceTypeSearchQuery(searchLower)) {
        return logSourceTypeMatchesSearch(log.source, searchLower);
      }
      if (log.AccountId?.toLowerCase().includes(searchLower)) return true;
      if (log.workspaceName?.toLowerCase().includes(searchLower)) return true;
      return log.logsSelected?.some((arn) =>
        logEntryMatchesSearch(arn, searchLower),
      );
    },
    [logEntryMatchesSearch],
  );

  const getVisibleLogEntries = useCallback(
    (log) => {
      const entries = log.logsSelected || [];
      const trimmedSearch = searchQuery.trim();

      if (strictLogFilter) {
        if (pipelineLogPaths.length > 0) {
          return entries.filter((arn) =>
            pipelineLogPaths.some((path) =>
              logArnMatchesFilterPath(arn, path, true),
            ),
          );
        }
        if (pipelineFilterName) {
          return entries.filter((arn) =>
            logArnMatchesPipelineName(arn, pipelineFilterName),
          );
        }
        return entries;
      }

      if (!trimmedSearch || isServiceTypeSearchQuery(trimmedSearch)) {
        return entries;
      }
      const searchLower = trimmedSearch.toLowerCase();
      return entries.filter((arn) => logEntryMatchesSearch(arn, searchLower));
    },
    [
      searchQuery,
      strictLogFilter,
      pipelineLogPaths,
      pipelineFilterName,
      logEntryMatchesSearch,
    ],
  );

  const logsData = useMemo(() => {
    let filtered = allLogs;

    if (filterService !== "All Services") {
      filtered = filtered.filter((log) => {
        const selected = normalizeLogSourceType(filterService);
        return normalizeLogSourceType(log.source) === selected;
      });
    }

    if (activeFilters.account) {
      filtered = filtered.filter((log) => log.AccountId === activeFilters.account);
    }

    if (strictLogFilter) {
      if (pipelineLogPaths.length > 0) {
        filtered = filtered.filter((log) =>
          log.logsSelected?.some((arn) =>
            pipelineLogPaths.some((path) =>
              logArnMatchesFilterPath(arn, path, true),
            ),
          ),
        );
      } else if (pipelineFilterName) {
        filtered = filtered.filter((log) =>
          log.logsSelected?.some((arn) =>
            logArnMatchesPipelineName(arn, pipelineFilterName),
          ),
        );
      }
      filtered = filtered.filter(
        (log) => getVisibleLogEntries(log).length > 0,
      );
    } else if (searchQuery.trim()) {
      const trimmedSearch = searchQuery.trim();
      const searchLower = trimmedSearch.toLowerCase();
      filtered = filtered.filter((log) => logSourceMatchesSearch(log, searchLower));
      if (!isServiceTypeSearchQuery(trimmedSearch)) {
        filtered = filtered.filter((log) => getVisibleLogEntries(log).length > 0);
      }
    }

    return filtered;
  }, [
    allLogs,
    filterService,
    searchQuery,
    activeFilters,
    strictLogFilter,
    pipelineLogPaths,
    pipelineFilterName,
    logSourceMatchesSearch,
    getVisibleLogEntries,
  ]);

  // Calculate statistics based on displayed/filtered data
  const statistics = useMemo(() => {
    const total = logsData.length;
    const cloudwatchLogs = logsData.filter((log) =>
      isCloudWatchLogSource(log.source),
    );
    const s3Logs = logsData.filter((log) => isS3LogSource(log.source));
    const cloudwatch = cloudwatchLogs.length;
    const s3 = s3Logs.length;
    const cloudwatchEntries = cloudwatchLogs.reduce(
      (sum, log) => sum + getVisibleLogEntries(log).length,
      0,
    );
    const s3Entries = s3Logs.reduce(
      (sum, log) => sum + getVisibleLogEntries(log).length,
      0,
    );
    const totalLogEntries = logsData.reduce(
      (sum, log) => sum + getVisibleLogEntries(log).length,
      0,
    );
    return { total, cloudwatch, s3, cloudwatchEntries, s3Entries, totalLogEntries };
  }, [logsData, getVisibleLogEntries]);

  const handleAccountChange = (value) => {
    setSelectedAccount(value);
    const account = accountOptions.find(opt => opt.value === value);
    if (account) {
      setSelectedAccountType(account.type);
      setActiveTab(account.type);
      // Reset filter based on account type
      if (account.type === 'databricks') {
        setFilterService('s3');
        const dbAccount = databricksAccounts.find(acc => acc.accountId === account.accountId);
        setSelectedDatabricksAccount(dbAccount);
      } else {
        setFilterService('All Services');
        setSelectedDatabricksAccount(null);
      }
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Find first account of the selected type
    const accountOfType = accountOptions.find(opt => opt.type === key);
    if (accountOfType) {
      setSelectedAccount(accountOfType.value);
      setSelectedAccountType(key);
      if (key === 'databricks') {
        setFilterService('s3');
        const dbAccount = databricksAccounts.find(acc => acc.accountId === accountOfType.accountId);
        setSelectedDatabricksAccount(dbAccount);
      } else {
        setFilterService('All Services');
        setSelectedDatabricksAccount(null);
      }
    } else {
      // If no account of this type, clear selection
      setSelectedAccount(null);
      setSelectedAccountType(key);
      setAllLogs([]);
    }
  };

  const handleOpenDatabricksModal = async () => {
    if (!selectedDatabricksAccount) {
      message.error('Please select a Databricks account');
      return;
    }
    
    databricksForm.resetFields();
    setDatabricksWorkspaces([]);
    setDatabricksClusterLogPaths([]);
    
    try {
      // Fetch AWS accounts for the dropdown
      try {
        const allAccounts = await accountService.getAccounts();
        const mappedAwsAccounts = [];
        
        // Extract accounts from all clients
        for (const clientId in allAccounts) {
          for (const accountId in allAccounts[clientId]) {
            mappedAwsAccounts.push({ 
              ...allAccounts[clientId][accountId], 
              accountId: accountId,
              clientId: clientId
            });
          }
        }
        
        setAwsAccountsForDatabricks(mappedAwsAccounts);
      } catch (error) {
        console.error('Failed to fetch AWS accounts:', error);
      }

      // Get client_id directly from backend API response
      const clientId = selectedDatabricksAccount.clientId;
      
      if (!clientId) {
        message.error('Account information not found. Please refresh the page.');
        return;
      }

      // Get stored credentials for PAT token only
      const storedCreds = getStoredCredentials(selectedDatabricksAccount.accountId);

      // Fetch workspaces
      try {
        const workspacesData = await databricksService.getWorkspaces(selectedDatabricksAccount.accountId);
        setDatabricksWorkspaces(workspacesData || []);
      } catch (workspaceError) {
        console.error('Failed to fetch workspaces:', workspaceError);
        message.error('Unable to fetch workspaces. Please check your account configuration.');
        message.error('Unable to fetch workspaces. Please check your account configuration.');
        setDatabricksWorkspaces([]);
      }

      // Set default values
      const allAccounts = await accountService.getAccounts();
      let defaultAwsAccount = undefined;
      
      // Find first available AWS account from all clients
      for (const clientId in allAccounts) {
        const awsAccountIds = Object.keys(allAccounts[clientId] || {});
        if (awsAccountIds.length > 0) {
          const firstAccountId = awsAccountIds[0];
          const firstAccount = allAccounts[clientId][firstAccountId];
          defaultAwsAccount = `${firstAccount.aws_account_name} (${firstAccountId})`;
          break;
        }
      }

      databricksForm.setFieldsValue({
        awsAccount: defaultAwsAccount,
        workspace: undefined,
        patToken: '',
        auditLogs: false,
        realtimeLogs: false,
        auditLogsPath: undefined,
        clusterLogsPath: undefined,
      });

      setDatabricksModalVisible(true);
    } catch (error) {
      console.error('Failed to load account configuration:', error);
      message.error('Failed to load account configuration');
    }
  };

  const handleDatabricksWorkspaceChange = async (workspaceId) => {
    if (!workspaceId || !selectedDatabricksAccount) return;

    const workspace = databricksWorkspaces.find(w => w.workspace_id === workspaceId);
    if (!workspace) return;

    databricksForm.setFieldsValue({ clusterLogsPath: undefined });
    setDatabricksClusterLogPaths([]);

    try {
      let workspaceUrl = workspace.workspace_url || workspace.deployment_url;
      
      if (!workspaceUrl) {
        message.warning('Workspace URL not available');
        setDatabricksClusterLogPaths([]);
        return;
      }

      if (!workspaceUrl.startsWith('http://') && !workspaceUrl.startsWith('https://')) {
        workspaceUrl = `https://${workspaceUrl}`;
      }

      // Get PAT token from form or stored credentials
      const patToken = databricksForm.getFieldValue('patToken');
      
      if (!patToken) {
        message.warning('PAT token is required. Please enter PAT token in the form.');
        setDatabricksClusterLogPaths([]);
        return;
      }

      try {
        message.loading({ content: 'Fetching cluster log paths...', key: 'fetchingPaths' });
        const clusterPaths = await databricksService.getClusterLogPaths(
          workspaceUrl,
          patToken
        );
        const paths = clusterPaths.cluster_log_paths || [];
        setDatabricksClusterLogPaths(paths);
        
        if (paths.length === 0) {
          message.warning({ content: 'No cluster log paths found for this workspace', key: 'fetchingPaths' });
        } else {
          message.success({ content: `Found ${paths.length} cluster log path(s)`, key: 'fetchingPaths' });
        }
      } catch (error) {
        console.error('Failed to fetch cluster log paths:', error);
        message.error({ 
          content: `Failed to fetch cluster log paths: ${error.message || 'Please check your PAT token and workspace URL'}`, 
          key: 'fetchingPaths',
          duration: 5
        });
        setDatabricksClusterLogPaths([]);
      }
    } catch (error) {
      console.error('Failed to handle workspace change:', error);
      setDatabricksClusterLogPaths([]);
    }
  };

  const handleSaveDatabricksConfiguration = async (values) => {
    try {
      const selectedWorkspace = databricksWorkspaces.find(
        w => w.workspace_id === values.workspace
      );

      if (!selectedWorkspace) {
        message.error('Please select a valid workspace');
        return;
      }

      // Validate at least one log type is selected
      if (!values.auditLogs && !values.realtimeLogs) {
        message.error('Please select at least one log type');
        return;
      }

      // Validate audit log path if audit logs is enabled
      if (values.auditLogs) {
        if (!values.auditLogsPath) {
          message.error('Please enter audit log path');
          return;
        }
      }

      // Validate cluster log path if realtime logs is enabled
      if (values.realtimeLogs && !values.clusterLogsPath) {
        message.error('Please select a cluster log path');
        return;
      }

      // Get client_id directly from backend API response
      const clientId = selectedDatabricksAccount.clientId;
      
      if (!clientId) {
        message.error('Account information not found. Please refresh the page.');
        return;
      }

      // PAT token must be entered by user (not from localStorage)
      const patToken = values.patToken;
      if (!patToken) {
        message.error('PAT token is required. Please enter PAT token.');
        return;
      }

      // Get AWS account ID from form selection
      if (!values.awsAccount) {
        message.error('Please select an AWS account');
        return;
      }

      // Extract AWS account ID from the selected value (format: "Account Name (accountId)")
      const awsAccountMatch = values.awsAccount.match(/\((\d+)\)$/);
      if (!awsAccountMatch) {
        message.error('Invalid AWS account selection');
        return;
      }
      const selectedAwsAccountId = awsAccountMatch[1];

      // Prepare payload for /databricks/onboard API
      // Only include paths for selected log types
      const onboardPayload = {
        client_id: clientId,
        workspace_name: selectedWorkspace.workspace_name || `Workspace ${selectedWorkspace.workspace_id}`,
        token: patToken,
        aws_account_id: selectedAwsAccountId,
        cluster_log_path: values.realtimeLogs ? (values.clusterLogsPath || null) : null,
        audit_log_path: values.auditLogs ? (values.auditLogsPath || null) : null,
        // Add Databricks account details for enhanced storage
        account_id: selectedDatabricksAccount?.accountId || null,
        account_url: selectedDatabricksAccount?.accountUrl || 'https://accounts.cloud.databricks.com',
      };

      // Call POST /databricks/onboard to onboard/update logs
      setLoading(true);
      try {
        await databricksService.onboardLogs(onboardPayload);

        message.success(editingLog ? 'Log configuration updated successfully' : 'Log configuration saved successfully');
        setDatabricksModalVisible(false);
        databricksForm.resetFields();
        setDatabricksWorkspaces([]);
        setDatabricksClusterLogPaths([]);
        setEditingLog(null);
        
        // Refetch databricks accounts to ensure we have the latest data
        try {
          const databricksAccountsList = await databricksService.getAccounts();
          const accountsWithDetails = databricksAccountsList.map((account) => {
            const accountId = account.account_id;
            const accountName = account.account_name || `Databricks Account ${accountId}`;
            const accountUrl = account.account_url || 'https://accounts.cloud.databricks.com';
            return {
              value: `databricks_${accountId}`,
              label: accountName,  // Display account name instead of account ID
              type: 'databricks',
              accountId: accountId,
              accountName: accountName,
              clientId: account.client_id,
              accountUrl: accountUrl,
            };
          });
          setDatabricksAccounts(accountsWithDetails);
          
          // Update account options with fresh databricks accounts
          const awsOptions = accountOptions.filter(opt => opt.type === 'aws');
          const allOptions = [...awsOptions, ...accountsWithDetails];
          setAccountOptions(allOptions);
        } catch (error) {
          console.error("Error refreshing databricks accounts:", error);
        }
        
        // Wait a bit for backend to process the deployment, then refresh logs
        setTimeout(async () => {
          await fetchLogs(selectedAccount);
        }, 1500);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      message.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
      setLoading(false);
    }
  };

  const handleAddLogEntry = () => {
    const accountId = form.getFieldValue("accountId");
    if (!accountId) {
      message.error("Please select an account first");
      return;
    }

const logArn = currentArnInput;
const service = form.getFieldValue("service");
const validationError = validateArn(logArn, accountId, service);

    if (validationError) {
      message.error(validationError);
      return;
    }

    const newEntry = { arn: logArn.trim() };

    if (
      logEntries.some(
        (entry, index) => entry.arn === newEntry.arn && index !== editingIndex
      )
    ) {
      message.warning("This Log ARN has already been added.");
      return;
    }
    let updatedEntries;
    if (editingIndex !== null) {
      updatedEntries = [...logEntries];
      updatedEntries[editingIndex] = newEntry;
      setEditingIndex(null);
    } else {
      updatedEntries = [...logEntries, newEntry];
    }
    setLogEntries(updatedEntries);
    setIsAccountLocked(true);
    setCurrentArnInput("");
  };

  const handleTagClick = (entry, index) => {
    setCurrentArnInput(entry.arn);
    setEditingIndex(index);
  };

  const handleDeleteLogEntry = (indexToDelete) => {
    const entryToDelete = logEntries[indexToDelete];
    const updatedEntries = logEntries.filter((_, i) => i !== indexToDelete);
    setLogEntries(updatedEntries);

    if (editingIndex === indexToDelete) {
      setCurrentArnInput("");
      setEditingIndex(null);
    }
  };

  const resetModalState = () => {
    form.resetFields();
    setLogEntries([]);
    setEditingLog(null);
    setIsAccountLocked(false);
    setEditingIndex(null);
    setCurrentArnInput("");
  };

  const handleSaveLogs = async () => {
    try {
      const fields = ["accountId", "service"];
      if (form.getFieldValue("service")?.toLowerCase() === "cloudwatch") {
        fields.push("cloudwatchRegion");
      }
      const values = await form.validateFields(fields);
      let currentArns = logEntries.map((entry) => entry.arn);

      // Convert log group names to full ARNs for CloudWatch (backend expects ARN format)
      const awsAccountNumeric = (values.accountId || "").replace(/^aws_/, "");

      if (values.service?.toLowerCase() === "cloudwatch" && awsAccountNumeric && values.cloudwatchRegion) {
        currentArns = currentArns.map((entry) =>
          toCloudWatchArn(entry, awsAccountNumeric, values.cloudwatchRegion || "us-east-1")
        );
      }

      if (
        values.service && values.service.toLowerCase() === "cloudwatch" &&
        currentArns.length === 0 &&
        !editingLog
      ) {
        message.error("Please add at least one log entry for CloudWatch");
        return;
      }

      if (
        values.service?.toLowerCase() === "s3" &&
        currentArns.length === 0 &&
        !editingLog
      ) {
        message.error("Please add at least one S3 URI");
        return;
      }

      setLoading(true);

      if (editingLog) {
        const originalArns = editingLog.logsSelected || [];
        const arnsToDelete = originalArns.filter(
          (arn) => !currentArns.includes(arn)
        );
        const arnsToAdd = currentArns.filter(
          (arn) => !originalArns.includes(arn)
        );
        let deleteSuccess = true;
        let addSuccess = true;

        if (arnsToDelete.length > 0) {
          const deletePayload = {
            client_id: editingLog.client_id,
            aws_account_id: editingLog.AccountId,
            source: editingLog.source,
            log_selector: arnsToDelete,
          };
          try {
            await logsService.deleteLogs(deletePayload);
          } catch (error) {
            deleteSuccess = false;
            message.error(`Error removing log entries: ${error.message}`);
          }
        }

        if (addSuccess && arnsToAdd.length > 0) {
          const addPayload = {
            client_id: user.client_id,
            aws_account_id: awsAccountNumeric,
            source: editingLog.source,
            log_selector: arnsToAdd,
          };
          try {
            await logsService.createLogs(addPayload);
          } catch (error) {
            addSuccess = false;
            showLogOnboardError(error, "adding new log entries");
          }
        }

        if (!deleteSuccess || !addSuccess) {
          setLoading(false);
          return;
        }

        if (arnsToDelete.length > 0 || arnsToAdd.length > 0) {
          message.success("Log configuration updated successfully.");
        } else {
          message.info("No changes detected in log entries.");
        }
      } else {
        const createPayload = {
          client_id: user.client_id,
          aws_account_id: awsAccountNumeric,
          source: values.service,
          log_selector: currentArns,
        };
        try {
          await logsService.createLogs(createPayload);
          message.success("Logs created successfully");
        } catch (error) {
          showLogOnboardError(error, "creating logs");
          setLoading(false);
          return;
        }
      }

      setModalVisible(false);
      await fetchLogs(selectedAccount);
      resetModalState();
    } catch (error) {
      if (error.message) {
        console.error("Save/Update Error:", error);
      } else if (error.errorFields) {
        console.error("Form validation failed:", error.errorFields);
      } else {
        console.error("An unexpected error occurred:", error);
      }
      setLoading(false);
    }
  };

  const handleEditLogs = async (record, isViewMode = false) => {
    // If it's a Databricks log, open the Databricks Configure Logs modal
    if (record.accountType === 'databricks') {
      // Find the Databricks account
      const accountId = record.AccountId;
      const dbAccount = databricksAccounts.find(acc => acc.accountId === accountId);
      
      if (!dbAccount) {
        message.error('Databricks account not found');
        return;
      }

      setSelectedDatabricksAccount(dbAccount);
      setEditingLog({ ...record, isViewMode });
      
      // Fetch current logs to get the configuration
      try {
        const logsData = await logsService.getDatabricksLogs(record.workspaceName);
        
        if (logsData) {
          // Get client_id directly from backend API response
          const clientId = dbAccount?.clientId;
          
          if (!clientId) {
            message.error('Account information not found. Please refresh the page.');
            return;
          }

          // Get stored credentials for PAT token only
          const storedCreds = getStoredCredentials(accountId);

          // Fetch workspaces
          try {
            const workspacesData = await databricksService.getWorkspaces(accountId);
            setDatabricksWorkspaces(workspacesData || []);
            
            // Find the workspace
            const workspace = workspacesData.find(w => 
              (w.workspace_name || `Workspace ${w.workspace_id}`) === record.workspaceName
            );

            if (workspace) {
              // If cluster logs exist, add the existing path to the dropdown
              // This allows editing without requiring a PAT token immediately
              if (logsData.cluster_logs && logsData.cluster_logs.log_path) {
                setDatabricksClusterLogPaths([logsData.cluster_logs.log_path]);
              } else {
                setDatabricksClusterLogPaths([]);
              }

              // Fetch AWS accounts for the dropdown
              try {
                const allAccounts = await accountService.getAccounts();
                const mappedAwsAccounts = [];
                
                // Extract accounts from all clients
                for (const clientId in allAccounts) {
                  for (const accountId in allAccounts[clientId]) {
                    mappedAwsAccounts.push({ 
                      ...allAccounts[clientId][accountId], 
                      accountId: accountId,
                      clientId: clientId
                    });
                  }
                }
                setAwsAccountsForDatabricks(mappedAwsAccounts);
                
                // Pre-fill AWS account if available
                const awsAccountValue = logsData.aws_account_id 
                  ? (() => {
                      const awsAccount = mappedAwsAccounts.find(acc => acc.accountId === logsData.aws_account_id);
                      return awsAccount 
                        ? `${awsAccount.aws_account_name} (${logsData.aws_account_id})`
                        : undefined;
                    })()
                  : undefined;

                // Pre-fill the form with existing values
                databricksForm.setFieldsValue({
                  awsAccount: awsAccountValue,
                  workspace: workspace.workspace_id,
                  patToken: '',
                  auditLogs: !!logsData.audit_logs?.log_path,
                  realtimeLogs: !!logsData.cluster_logs?.log_path,
                  auditLogsPath: logsData.audit_logs?.log_path || 's3://databricks-logs-observability/audit',
                  clusterLogsPath: logsData.cluster_logs?.log_path || undefined,
                });
              } catch (error) {
                console.error('Failed to fetch AWS accounts:', error);
                // Still set other values
                databricksForm.setFieldsValue({
                  workspace: workspace.workspace_id,
                  patToken: '',
                  auditLogs: !!logsData.audit_logs?.log_path,
                  realtimeLogs: !!logsData.cluster_logs?.log_path,
                  auditLogsPath: logsData.audit_logs?.log_path || 's3://databricks-logs-observability/audit',
                  clusterLogsPath: logsData.cluster_logs?.log_path || undefined,
                });
              }
            }
          } catch (workspaceError) {
            console.error('Failed to fetch workspaces:', workspaceError);
            message.error('Unable to fetch workspaces. Please check your credentials.');
            setDatabricksWorkspaces([]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch log configuration:', error);
        message.error('Failed to load log configuration');
        return;
      }

      setDatabricksModalVisible(true);
    } else {
      // AWS logs - use existing edit flow
      setEditingLog(record);
      const firstArn = record.logsSelected?.[0];
      const regionFromArn = firstArn?.startsWith("arn:aws:logs:")
        ? firstArn.split(":")[3]
        : "us-east-1";
      form.setFieldsValue({
        accountId: record.AccountId,
        service: record.source,
        cloudwatchRegion: record.source?.toLowerCase() === "cloudwatch" ? regionFromArn : undefined,
      });
      setCurrentArnInput("");
      setLogEntries(
        JSON.parse(JSON.stringify(record.logsSelected.map((arn) => ({ arn }))))
      );
      setIsAccountLocked(true);
      setEditingIndex(null);
      setModalVisible(true);
    }
  };

  const handleDeleteLogs = async (record) => {
    if (!record) {
      return;
    }

    setLoading(true);
    try {
      if (record.accountType === 'databricks') {
        // For Databricks logs, use deboard endpoint
        if (!record.workspaceName) {
          message.error('Workspace name not found');
          setLoading(false);
          return;
        }
        await databricksService.deboardLogs(record.workspaceName);
        message.success("Databricks log configuration deleted successfully");
      } else {
        // For AWS logs, use existing delete endpoint
        if (!record.logsSelected || record.logsSelected.length === 0) {
          message.error('No logs to delete');
          setLoading(false);
          return;
        }
        const deletePayload = {
          client_id: record.client_id,
          aws_account_id: record.AccountId,
          source: record.source,
          log_selector: record.logsSelected,
        };
        await logsService.deleteLogs(deletePayload);
        message.success("Log deleted successfully");
      }
      await fetchLogs(selectedAccount);
    } catch (error) {
      message.error("Error deleting logs: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedSources.length === logsData.length && logsData.length > 0) {
      setSelectedSources([]);
    } else {
      setSelectedSources(logsData.map(log => log.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedSources(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const removeFilter = (key) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSourceClick = (record) => {
    setSelectedSourceDetail(record);
    setShowDetailDrawer(true);
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedSources.length === logsData.length && logsData.length > 0}
          indeterminate={selectedSources.length > 0 && selectedSources.length < logsData.length}
          onChange={toggleSelectAll}
        />
      ),
      key: "selection",
      width: 48,
      render: (_, record) => (
        <Checkbox
          checked={selectedSources.includes(record.id)}
          onChange={() => toggleSelect(record.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    { 
      title: "Account ID", 
      dataIndex: "AccountId", 
      key: "AccountId",
      render: (text, record) => {
        // For Databricks logs, show Databricks account ID
        if (record?.accountType === 'databricks') {
          return <span>{highlightSearchText(text)}</span>;
        }
        // For AWS logs, show AWS account ID
        return <span>{highlightSearchText(text)}</span>;
      }
    },
    {
      title: "Workspace / Service",
      dataIndex: "source",
      key: "source",
      render: (text, record) => {
        if (record?.accountType === 'databricks') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Badge status="success" />
            <span style={{ color: isDark ? "#e6edf3" : "#000", fontWeight: 500 }}>
              {highlightSearchText(record.workspaceName || 'N/A')}
            </span>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Badge 
              status={text === 'CloudWatch' ? 'processing' : 'default'} 
              color={text === 'CloudWatch' ? '#1890ff' : '#52c41a'}
            />
            <span style={{ color: isDark ? "#e6edf3" : "#000", fontWeight: 500 }}>{highlightSearchText(text)}</span>
          </div>
        );
      },
    },
    {
      title: "Log",
      dataIndex: "logsSelected",
      key: "log",
      width: "40%",
      render: (logs, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {logs?.map((arn, idx) => {
            const { log_group, bucket, path } = parseArn(arn);
            
            // For S3 logs, display bucket and path on the same line
            if (record.source === "s3" && bucket) {
              return (
                <div
                  key={idx + "-" + bucket}
                  style={{
                    fontSize: "12px",
                    padding: "6px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    backgroundColor: isDark ? "#1c2128" : "#fafafa",
                    border: isDark ? "1px solid #30363d" : "1px solid #d9d9d9"
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    <div style={{ 
                      display: "flex",
                      marginRight: "8px",
                      minWidth: "0",
                      flex: "1"
                    }}>
                      <span style={{ 
                        fontWeight: "bold", 
                        color: "#1890ff", 
                        marginRight: "4px",
                        flexShrink: 0
                      }}>Bucket:</span> 
                      <span style={{ 
                        overflow: "hidden", 
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: "0"
                      }}>
                        {highlightSearchText(bucket)}
                      </span>
                    </div>
                    {path && (
                      <div style={{ 
                        display: "inline-flex",
                        flex: "1",
                        whiteSpace: "nowrap" 
                      }}>
                        <span style={{ 
                          fontWeight: "bold", 
                          color: "#52c41a", 
                          marginRight: "4px" 
                        }}>Path:</span>
                        <span style={{ 
                          overflow: "hidden", 
                          textOverflow: "ellipsis",
                          maxWidth: "180px"
                        }}>
                          {highlightSearchText(path)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            
            // For CloudWatch logs, display log group
            return (
              <div
                key={idx + "-" + log_group}
                style={{
                  padding: "4px 8px",
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  wordBreak: "break-all"
                }}
              >
                {highlightSearchText(log_group)}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: "Log Entries",
      dataIndex: "logsSelected",
      key: "logCount",
      width: 100,
      render: (logs) => (
        <Badge count={logs?.length || 0} showZero style={{ backgroundColor: '#1890ff' }} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button          
              icon={<EyeOutlined />}
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSourceClick(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              icon={<EditFilled />}
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditLogs(record);
              }}
              disabled={isViewer}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Log"
            description="Are you sure you want to delete this log?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteLogs(record);
            }}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Tooltip title="Delete">
              <Button 
                icon={<DeleteFilled />}
                type="text" 
                danger 
                size="small"
                disabled={isViewer}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Tabs
        className="page-tabs-fill"
        activeKey={activeTab}
        onChange={handleTabChange}
        destroyInactiveTabPane={true}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        items={[
          {
            key: 'aws',
            label: (
              <span style={{ color: activeTab === 'aws' ? '#1890ff' : (isDark ? '#adbac7' : '#666') }}>
                AWS Logs
              </span>
            ),
            children: (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Page Header - static */}
                <div style={{ marginBottom: 24, flexShrink: 0 }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 20,
                    flexWrap: "wrap",
                    gap: "16px"
                  }}>
                    <div>
                      <Title level={2} style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
                        Log Source Management
                      </Title>
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        Onboard, configure, and monitor your log sources across cloud providers
                      </Text>
                    </div>
                    <Space wrap>
                    <Select
                      placeholder="Select Account"
                      options={accountOptions.filter(opt => opt.type === 'aws')}
                        style={{ width: 280 }}
                      onChange={handleAccountChange}
                      value={selectedAccount}
                        size="large"
                    />
                    <Tooltip title="Refresh Logs">  
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => fetchLogs(selectedAccount)}
                      loading={loading}
                      disabled={!selectedAccount}
                          size="large"
                    >
                          Refresh
                    </Button>
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                      onClick={() => {
                          setShowOnboardingWizard(true);
                          setWizardStep(1);
                        resetModalState();
                        if (selectedAccount) {
                          const accountId = selectedAccount.replace('aws_', '');
                          form.setFieldsValue({ accountId: accountId });
                        }
                      }}
                      disabled={!selectedAccount || isViewer}
                    >
                        Onboard Source
                    </Button>
                  </Space>
                </div>

                  {/* Statistics Cards - always visible; show 0 when no account selected */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Sources</span>}
                          value={selectedAccount ? statistics.total : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                          prefix={<DatabaseOutlined style={{ color: '#fff', fontSize: 24 }} />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>CloudWatch</span>}
                          value={selectedAccount ? statistics.cloudwatchEntries : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                          suffix={<span style={{ fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>entries</span>}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>S3 Logs</span>}
                          value={selectedAccount ? statistics.s3Entries : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                          suffix={<span style={{ fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>entries</span>}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Entries</span>}
                          value={selectedAccount ? statistics.totalLogEntries : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* Search and Filters */}
                  <Card 
                    bordered={false}
                    style={{ 
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: 16
                    }}
                  >
                    <div style={{ display: "flex", gap: 12, marginBottom: Object.keys(activeFilters).length > 0 ? 16 : 0, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 300 }}>
                        <Search
                          bordered={false}
                          placeholder="Search log sources by name, account, service, bucket, or log group..."
                          allowClear
                          enterButton
                          size="large"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onSearch={setSearchQuery}
                        />
                      </div>
                      
                      <Select
                        placeholder="Filter by Service"
                        style={{ width: 180 }}
                        value={filterService}
                        onChange={(value) => setFilterService(value)}
                        size="large"
                        options={[
                          { label: "All Services", value: "All Services" },
                          { label: "S3 Logs", value: "s3" },
                          { label: "CloudWatch Logs", value: "CloudWatch" },
                        ]}
                      />

                      <Select
                        placeholder="Filter by Account"
                        style={{ width: 200 }}
                        size="large"
                        allowClear
                        value={activeFilters.account}
                        onChange={(value) => {
                          if (value) {
                            setActiveFilters(prev => ({ ...prev, account: value }));
                          } else {
                            removeFilter('account');
                          }
                        }}
                        options={Array.from(new Set(allLogs.map(log => log.AccountId))).map(acc => ({
                          label: acc,
                          value: acc
                        }))}
                      />
                    </div>

                    {/* Active Filters */}
                    {Object.keys(activeFilters).length > 0 && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>Active filters:</Text>
                        {Object.entries(activeFilters).map(([key, value]) => (
                          <Tag
                            key={key}
                            closable
                            onClose={() => removeFilter(key)}
                            color="blue"
                            style={{ fontSize: 13 }}
                          >
                            {key}: {value}
                          </Tag>
                        ))}
                        <Button 
                          type="link" 
                          size="small"
                          onClick={() => setActiveFilters({})}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          Clear all
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Bulk Actions */}
                  {selectedSources.length > 0 && (
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        marginBottom: 16,
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.2)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: 14 }}>
                          <span style={{ color: '#1890ff' }}>{selectedSources.length}</span> source{selectedSources.length > 1 ? 's' : ''} selected
                        </Text>
                        <Space>
                          <Button size="small" icon={<DeleteFilled />} danger>
                            Delete Selected
                          </Button>
                          <Button size="small" onClick={() => setSelectedSources([])}>
                            Clear Selection
                          </Button>
                        </Space>
                      </div>
                    </Card>
                  )}
                </div>

                {/* Card-based Layout - scrollable */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {loading ? (
                  <Card style={{ textAlign: 'center', padding: '40px' }}>
                    <Empty description="Loading logs..." />
                  </Card>
                ) : logsData.length === 0 ? (
                  <Card style={{ textAlign: 'center', padding: '40px' }}>
                    <Empty 
                      description="No log sources found"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {logsData.map((record) => {
                      const isSelected = selectedSources.includes(record.id);
                      const visibleEntries = getVisibleLogEntries(record);
                      const logCount = visibleEntries.length;
                      
                      return (
                        <Card
                          key={record.id}
                          bordered={false}
                          style={{
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: isSelected ? '2px solid #1890ff' : (isDark ? '1px solid #30363d' : '1px solid #e8e8e8'),
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: isSelected ? (isDark ? '#1c2128' : '#f0f7ff') : (isDark ? '#161b22' : '#fff')
                          }}
                          onClick={() => handleSourceClick(record)}
                          hoverable
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleSelect(record.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              
                              <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                background: isCloudWatchLogSource(record.source)
                                  ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                                  : 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {isCloudWatchLogSource(record.source) ? (
                                  <CloudOutlined style={{ fontSize: 20, color: '#fff' }} />
                                ) : (
                                  <DatabaseOutlined style={{ fontSize: 20, color: '#fff' }} />
                                )}
                              </div>
                              
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                                  <Text strong style={{ fontSize: 16 }}>
                                    {record.accountType === 'databricks' 
                                      ? record.workspaceName 
                                      : record.source}
                                  </Text>
                                  <Badge 
                                    count={logCount} 
                                    style={{ backgroundColor: '#1890ff' }}
                                  />
                                  <Tag color={isCloudWatchLogSource(record.source) ? 'blue' : 'green'}>
                                    {normalizeLogSourceType(record.source)}
                                  </Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                  Account: {record.accountType === 'databricks' && record.accountName 
                                    ? record.accountName 
                                    : record.AccountId}
                                </Text>
                              </div>
                            </div>
                            
                            <Space onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="View Details">
                                <Button
                                  icon={<EyeOutlined />}
                                  type="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSourceClick(record);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Button
                                  icon={<EditFilled />}
                                  type="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditLogs(record);
                                  }}
                                  disabled={isViewer}
                                />
                              </Tooltip>
                              <Popconfirm
                                title="Delete Log Source"
                                description="Are you sure you want to delete this log source?"
                                onConfirm={(e) => {
                                  e?.stopPropagation();
                                  handleDeleteLogs(record);
                                }}
                                okText="Yes"
                                cancelText="No"
                                onCancel={(e) => e?.stopPropagation()}
                              >
                                <Tooltip title="Delete">
                                  <Button
                                    icon={<DeleteFilled />}
                                    type="text"
                                    danger
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isViewer}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            </Space>
                          </div>
                          
                          {/* Log Entries Preview */}
                          {visibleEntries.length > 0 && (
                            <div style={{ 
                              marginTop: 16, 
                              paddingTop: 16, 
                              borderTop: '1px solid #f0f0f0' 
                            }}>
                              <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                                Log Entries ({logCount}):
                              </Text>
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 8,
                                maxHeight: 120,
                                overflowY: 'auto'
                              }}>
                                {visibleEntries.slice(0, 5).map((arn, idx) => {
                                  const { log_group, bucket, path } = parseArn(arn);
                                  
                                  if (isS3LogSource(record.source) && bucket) {
                                    return (
                                      <Tag 
                                        key={idx} 
                                        className="logs-page__path-tag logs-page__path-tag--s3"
                                        style={{ margin: 0, fontSize: 12 }}
                                      >
                                        {highlightSearchText(`${bucket}${path ? path : ""}`)}
                                      </Tag>
                                    );
                                  }
                                  
                                  return (
                                    <Tag 
                                      key={idx} 
                                      className="logs-page__path-tag logs-page__path-tag--cloudwatch"
                                      style={{ margin: 0, fontSize: 12, maxWidth: 300 }}
                                    >
                                      {highlightSearchText(log_group)}
                                    </Tag>
                                  );
                                })}
                                {logCount > 5 && (
                                  <Tag style={{ margin: 0, fontSize: 12 }}>
                                    +{logCount - 5} more
                                  </Tag>
                                )}
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            ),
          },
          {
            key: 'databricks',
            label: (
              <span style={{ color: activeTab === 'databricks' ? '#1890ff' : (isDark ? '#adbac7' : '#666') }}>
                Databricks Logs
              </span>
            ),
            children: (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Page Header - static */}
                <div style={{ marginBottom: 24, flexShrink: 0 }}>
                  <div style={{ 
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 20,
                    flexWrap: "wrap",
                    gap: "16px"
                  }}>
                    <div>
                      <Title level={2} style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
                        Databricks Log Source Management
                      </Title>
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        Onboard, configure, and monitor your Databricks workspace log sources
                      </Text>
                    </div>
                  <Space wrap>
                    <Select
                        placeholder="Select Databricks Account"
                      options={accountOptions.filter(opt => opt.type === 'databricks')}
                      style={{ width: 350, minWidth: 350 }}
                      onChange={handleAccountChange}
                      value={selectedAccount}
                      showSearch
                        size="large"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownStyle={{ minWidth: 350 }}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      popupMatchSelectWidth={false}
                    />
                    <Tooltip title="Refresh Logs">  
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => fetchLogs(selectedAccount)}
                      loading={loading}
                      disabled={!selectedAccount}
                          size="large"
                    >
                          Refresh
                    </Button>
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                      onClick={() => {
                        handleOpenDatabricksModal();
                      }}
                      disabled={!selectedAccount || isViewer}
                    >
                        Onboard Source
                    </Button>
                  </Space>
                </div>

                  {/* Statistics Cards - always visible; show 0 when no account selected */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={8}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Workspaces</span>}
                          value={selectedAccount ? logsData.length : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                          prefix={<DatabaseOutlined style={{ color: '#fff', fontSize: 24 }} />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Log Paths</span>}
                          value={selectedAccount ? logsData.reduce((sum, log) => sum + (log.logsSelected?.length || 0), 0) : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Card 
                        bordered={false}
                        style={{ 
                          borderRadius: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        }}
                      >
                        <Statistic
                          title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Active Configs</span>}
                          value={selectedAccount ? logsData.filter(log => log.enabled !== false).length : 0}
                          valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* Search and Filters */}
                  <Card 
                    bordered={false}
                    style={{ 
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: 16
                    }}
                  >
                    <div style={{ display: "flex", gap: 12, marginBottom: Object.keys(activeFilters).length > 0 ? 16 : 0, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 300 }}>
                        <Search
                          bordered={false}
                          placeholder="Search by workspace name, bucket, or log path..."
                          allowClear
                          enterButton
                          size="large"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onSearch={setSearchQuery}
                        />
                      </div>
                      
                      <Select
                        placeholder="Filter by Account"
                        style={{ width: 200 }}
                        size="large"
                        allowClear
                        value={activeFilters.account}
                        onChange={(value) => {
                          if (value) {
                            setActiveFilters(prev => ({ ...prev, account: value }));
                          } else {
                            removeFilter('account');
                          }
                        }}
                        options={Array.from(new Set(allLogs.map(log => log.AccountId))).map(acc => ({
                          label: acc,
                          value: acc
                        }))}
                      />
                    </div>

                    {/* Active Filters */}
                    {Object.keys(activeFilters).length > 0 && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>Active filters:</Text>
                        {Object.entries(activeFilters).map(([key, value]) => (
                          <Tag
                            key={key}
                            closable
                            onClose={() => removeFilter(key)}
                            color="blue"
                            style={{ fontSize: 13 }}
                          >
                            {key}: {value}
                          </Tag>
                        ))}
                        <Button 
                          type="link" 
                          size="small"
                          onClick={() => setActiveFilters({})}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          Clear all
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Bulk Actions */}
                  {selectedSources.length > 0 && (
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        marginBottom: 16,
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.2)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: 14 }}>
                          <span style={{ color: '#1890ff' }}>{selectedSources.length}</span> source{selectedSources.length > 1 ? 's' : ''} selected
                        </Text>
                        <Space>
                          <Button size="small" icon={<DeleteFilled />} danger>
                            Delete Selected
                          </Button>
                          <Button size="small" onClick={() => setSelectedSources([])}>
                            Clear Selection
                          </Button>
                        </Space>
                      </div>
                    </Card>
                  )}
                </div>

                {/* Card-based Layout - scrollable */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {loading ? (
                  <Card style={{ textAlign: 'center', padding: '40px' }}>
                    <Empty description="Loading logs..." />
                  </Card>
                ) : logsData.length === 0 ? (
                  <Card style={{ textAlign: 'center', padding: '40px' }}>
                    <Empty 
                      description="No log sources found"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {logsData.map((record) => {
                      const isSelected = selectedSources.includes(record.id);
                      const visibleEntries = getVisibleLogEntries(record);
                      const logCount = visibleEntries.length;
                      
                      return (
                        <Card
                          key={record.id}
                          bordered={false}
                          style={{
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: isSelected ? '2px solid #1890ff' : (isDark ? '1px solid #30363d' : '1px solid #e8e8e8'),
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: isSelected ? (isDark ? '#1c2128' : '#f0f7ff') : (isDark ? '#161b22' : '#fff')
                          }}
                          onClick={() => handleSourceClick(record)}
                          hoverable
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleSelect(record.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              
                              <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <DatabaseOutlined style={{ fontSize: 20, color: '#fff' }} />
                              </div>
                              
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                                  <Text strong style={{ fontSize: 16 }}>
                                    {record.accountType === 'databricks' 
                                      ? record.workspaceName 
                                      : record.source}
                                  </Text>
                                  <Badge 
                                    count={logCount} 
                                    style={{ backgroundColor: '#1890ff' }}
                                  />
                                  <Tag color="purple">
                                    Databricks
                                  </Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                  Account: {record.accountType === 'databricks' && record.accountName 
                                    ? record.accountName 
                                    : record.AccountId} {record.awsAccountId && `• AWS: ${record.awsAccountId}`}
                                </Text>
                              </div>
                            </div>
                            
                            <Space onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="View Details">
                                <Button
                                  icon={<EyeOutlined />}
                                  type="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSourceClick(record);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Button
                                  icon={<EditFilled />}
                                  type="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditLogs(record);
                                  }}
                                  disabled={isViewer}
                                />
                              </Tooltip>
                              <Popconfirm
                                title="Delete Log Source"
                                description="Are you sure you want to delete this log source?"
                                onConfirm={(e) => {
                                  e?.stopPropagation();
                                  handleDeleteLogs(record);
                                }}
                                okText="Yes"
                                cancelText="No"
                                onCancel={(e) => e?.stopPropagation()}
                              >
                                <Tooltip title="Delete">
                                  <Button
                                    icon={<DeleteFilled />}
                                    type="text"
                                    danger
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isViewer}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            </Space>
                          </div>
                          
                          {/* Log Entries Preview */}
                          {record.logsSelected && record.logsSelected.length > 0 && (
                            <div style={{ 
                              marginTop: 16, 
                              paddingTop: 16, 
                              borderTop: '1px solid #f0f0f0' 
                            }}>
                              <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                                Log Paths ({logCount}):
                              </Text>
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 8,
                                maxHeight: 120,
                                overflowY: 'auto'
                              }}>
                                {record.logsSelected.slice(0, 5).map((path, idx) => (
                                  <Tag 
                                    key={idx} 
                                    color="purple"
                                    style={{ margin: 0, fontSize: 12, maxWidth: 400 }}
                                  >
                                    {path}
                                  </Tag>
                                ))}
                                {logCount > 5 && (
                                  <Tag style={{ margin: 0, fontSize: 12 }}>
                                    +{logCount - 5} more
                                  </Tag>
                                )}
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            ),
          },
        ]}
      />    
      <Modal
        title={editingLog ? "Edit Logs" : "Create Logs"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          resetModalState();
        }}
        onOk={handleSaveLogs}
        width={600}
        confirmLoading={loading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" name="log_config_form_modal" requiredMark={false}>
          <Form.Item
            name="accountId"
            label={<span>Account ID <span style={{ color: 'red' }}>*</span></span>}
            rules={[{ required: true, message: "Please select an account ID" }]}
          >
            <Select
              placeholder="Select Account ID"
              options={accountOptions.filter(opt => opt.type === 'aws').map(opt => ({
                value: opt.accountId,
                label: opt.label
              }))}
              disabled={!!selectedAccount || selectedAccountType === 'databricks'}
            />
          </Form.Item>
          <Form.Item
            name="service"
            label={<span>Service <span style={{ color: 'red' }}>*</span></span>}
            rules={[{ required: true, message: "Please select a service" }]}
          >
            <Select
              placeholder="Select Service"
              disabled={!!editingLog}
              onChange={(value) => {
                setCurrentArnInput("");
                setEditingIndex(null);
                if (!editingLog) {
                  setLogEntries([]);
                }
                setIsAccountLocked(logEntries.length > 0 || !!editingLog);
              }}
              options={
                selectedAccountType === 'databricks'
                  ? [{ label: "S3 Logs", value: "s3" }]
                  : [
                      { label: "S3", value: "s3" },
                      { label: "CloudWatch", value: "CloudWatch" },
                    ]
              }
            />
          </Form.Item>

          {form.getFieldValue("service") === "s3" ? (
            <>
              <Form.Item label={<span>S3 URI <span style={{ color: 'red' }}>*</span></span>} >
                <Input
                  placeholder="Enter S3 URI"
                  value={currentArnInput}
                  onChange={(e) => setCurrentArnInput(e.target.value)}
                  onPressEnter={handleAddLogEntry}
                />
              </Form.Item>
              <Button
                type="dashed"
                onClick={handleAddLogEntry}
                icon={<PlusOutlined />}
                style={{ width: "100%", marginBottom: 16 }}
                disabled={!currentArnInput.trim()}
              >
                {editingIndex !== null ? "Update Log Entry" : "Add Log Entry"}
              </Button>
              {logEntries.length > 0 && (
                <Form.Item label="Added Logs">
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap", 
                    gap: "8px"
                  }}>
                    {logEntries.map((entry, index) => (
                      <Tag
                        key={entry.arn}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          handleDeleteLogEntry(index);
                        }}
                        onClick={() => handleTagClick(entry, index)}
                        className="custom-log-tag"
                      >
                        {entry.arn}
                      </Tag>
                    ))}
                  </div>
                </Form.Item>
              )}
            </>
          ) : form.getFieldValue("service") && form.getFieldValue("service").toLowerCase() === "cloudwatch" ? (
            <>
              <Form.Item label={<span>Log Group ARN <span style={{ color: 'red' }}>*</span></span>}>
                <Input
                  placeholder="Enter log string"
                  value={currentArnInput}
                  onChange={(e) => setCurrentArnInput(e.target.value)}
                  onPressEnter={handleAddLogEntry}
                />
              </Form.Item>
              <Button
                type="dashed"
                onClick={handleAddLogEntry}
                icon={<PlusOutlined />}
                style={{ width: "100%", marginBottom: 16 }}
                disabled={!currentArnInput.trim()}
              >
                {editingIndex !== null ? "Update Log Entry" : "Add Log Entry"}
              </Button>
              {logEntries.length > 0 && (
                <Form.Item label="Added Logs">
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap", 
                    gap: "8px"
                  }}>
                    {logEntries.map((entry, index) => (
                      <Tag
                        key={entry.arn}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          handleDeleteLogEntry(index);
                        }}
                        onClick={() => handleTagClick(entry, index)}
                        className="custom-log-tag"
                      >
                        {entry.arn}
                      </Tag>
                    ))}
                  </div>
                </Form.Item>
              )}
            </>
          ) : null}
        </Form>
      </Modal>

      {/* View Logs Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px', fontSize: '18px' }}>
                {viewingLog?.source || "Log Details"}
              </span>
            </div>
            <Input.Search 
              placeholder="Filter logs" 
              style={{ width: 200, marginRight: '25px' }} 
              size="small"
              allowClear
              id="logs-search-input"
              onInput={(e) => {
                const searchText = e.target.value.toLowerCase();
                const logTags = document.querySelectorAll('.log-tag');
                logTags.forEach(tag => {
                  const logGroup = tag.getAttribute('data-log-group').toLowerCase();
                  if (logGroup.includes(searchText)) {
                    tag.style.display = '';
                  } else {
                    tag.style.display = 'none';
                  }
                });
              }}
            />
          </div>
        }
        open={viewModalVisible}
        onCancel={() => { 
          setViewModalVisible(false);
          setViewingLog(null);
        }}
        footer={[
          <Button 
            key="edit"
            icon={<EditFilled />}
            onClick={() => {
              setViewModalVisible(false);
              handleEditLogs(viewingLog);
            }}
            disabled={isViewer}
          >
            Edit Logs
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: '700px' }}
        destroyOnClose
        bodyStyle={{ padding: '16px', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {viewingLog && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card 
                  size="small"
                  bordered={false}
                  style={{ 
                    width: '100%',
                    maxWidth: '100%',
                    minHeight: '69px',
                    borderRadius: '8px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    transform: 'rotate(0deg)',
                    opacity: 1,
                    background: isDark ? '#1c2128' : '#F2F4F7',
                    border: isDark ? '1px solid #30363d' : 'none',
                    boxShadow: isDark 
                      ? '0px 1px 2px 0px rgba(0,0,0,0.3)' 
                      : '0px 1px 2px 0px #0000001A, -8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8',
                    marginBottom: '12px' 
                  }}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} sm={viewingLog.accountType === 'databricks' ? 12 : 8}>
                      <div style={{ fontSize: '13px', color: isDark ? '#adbac7' : '#666666', marginBottom: '2px' }}>
                        {viewingLog.accountType === 'databricks' ? 'Databricks Account' : 'Account ID'}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: isDark ? '#e6edf3' : '#333333', wordBreak: 'break-all' }}>
                        {viewingLog.accountType === 'databricks' && viewingLog.accountName 
                          ? viewingLog.accountName 
                          : viewingLog.AccountId}
                      </div>
                    </Col>
                    {viewingLog.accountType === 'databricks' && viewingLog.workspaceName && (
                      <Col xs={24} sm={12}>
                        <div style={{ fontSize: '13px', color: isDark ? '#adbac7' : '#666666', marginBottom: '2px' }}>Workspace</div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: isDark ? '#e6edf3' : '#333333' }}>{viewingLog.workspaceName}</div>
                    </Col>
                    )}
                    <Col xs={12} sm={viewingLog.accountType === 'databricks' ? 12 : 8}>
                      <div style={{ fontSize: '13px', color: isDark ? '#adbac7' : '#666666', marginBottom: '2px' }}>Service Type</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: isDark ? '#e6edf3' : '#333333' }}>{viewingLog.source}</div>
                    </Col>
                    {viewingLog.accountType === 'databricks' && viewingLog.awsAccountId && (
                      <Col xs={12} sm={12}>
                        <div style={{ fontSize: '13px', color: isDark ? '#adbac7' : '#666666', marginBottom: '2px' }}>AWS Account</div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: isDark ? '#e6edf3' : '#333333' }}>{viewingLog.awsAccountId}</div>
                      </Col>
                    )}
                    <Col xs={12} sm={viewingLog.accountType === 'databricks' ? 12 : 8}>
                      <div style={{ fontSize: '13px', color: isDark ? '#adbac7' : '#666666', marginBottom: '2px' }}>Log Count</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: isDark ? '#e6edf3' : '#333333' }}>{viewingLog.logsSelected?.length || 0}</div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              <Col span={24}>
                {viewingLog.logsSelected?.length > 0 ? (
                  <div className="logs-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {viewingLog.logsSelected.map((arn, idx) => {
                      const { region, log_group, bucket, path } = parseArn(arn);
                      
                      // For S3 logs
                      if (viewingLog.source === "s3" && bucket) {
                        return (
                          <div 
                            key={idx}
                            className="log-tag"
                            data-log-group={bucket}
                            style={{ 
                              padding: '8px 12px', 
                              borderRadius: '4px', 
                              backgroundColor: isDark ? '#1c2128' : '#f0f8ff',
                              borderLeft: '4px solid #0052cc',
                              border: isDark ? '1px solid #30363d' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              gap: '8px',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          >
                            <div style={{ flex: '1', overflow: 'hidden', minWidth: '0' }}>
                              <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px', color: isDark ? '#e6edf3' : '#000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bucket}
                              </div>
                              {path && (
                                <div style={{ fontSize: '12px', color: isDark ? '#adbac7' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <span style={{ fontWeight: 'bold', color: '#52c41a' }}>Path:</span> {path}
                                </div>
                              )}
                            </div>
                            <Tag color="#1890ff" style={{ margin: '0', whiteSpace: 'nowrap', flexShrink: 0 }}>{region}</Tag>
                          </div>
                        );
                      }
                      
                      // For CloudWatch logs
                      return (
                        <div 
                          key={idx}
                          className="log-tag"
                          data-log-group={log_group}
                          style={{ 
                            padding: '8px 12px', 
                            borderRadius: '4px', 
                            backgroundColor: isDark ? '#1c2128' : '#f5f5f5',
                            borderLeft: '4px solid #52c41a',
                            border: isDark ? '1px solid #30363d' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '8px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        >
                          <div style={{ flex: '1', overflow: 'hidden' }}>
                            <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px', color: isDark ? '#e6edf3' : '#333333', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {log_group !== "N/A" ? log_group : arn}
                            </div>
                            {arn !== log_group && (
                              <div style={{ fontSize: '12px', color: isDark ? '#8b949e' : '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {arn}
                            </div>
                            )}
                          </div>
                          {region !== "N/A" && (
                                                     <Tag color="#1990FF" style={{ margin: '0 4px 4px 0', fontSize: '12px' }}>{region}</Tag>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty 
                    description={<span style={{ color: isDark ? '#8b949e' : '#999' }}>No logs defined</span>} 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Databricks Configure Logs Modal */}
      <Modal
        title={editingLog && editingLog.isViewMode ? "View Logs" : editingLog ? "Edit Logs" : "Configure Logs"}
        open={databricksModalVisible}
        onCancel={() => {
          setDatabricksModalVisible(false);
          databricksForm.resetFields();
          setDatabricksWorkspaces([]);
          setDatabricksClusterLogPaths([]);
          setEditingLog(null);
        }}
        onOk={() => {
          if (!editingLog || !editingLog.isViewMode) {
            databricksForm.submit();
          } else {
            setDatabricksModalVisible(false);
            databricksForm.resetFields();
            setDatabricksWorkspaces([]);
            setDatabricksClusterLogPaths([]);
            setEditingLog(null);
          }
        }}
        okText={editingLog && editingLog.isViewMode ? "Close" : "OK"}
        cancelText="Cancel"
        width={600}
      >
        <Form
          form={databricksForm}
          layout="vertical"
          onFinish={(values) => {
            if (!editingLog || !editingLog.isViewMode) {
              handleSaveDatabricksConfiguration(values);
            }
          }}
        >
          <Form.Item
            name="awsAccount"
            label="AWS Account"
            rules={[{ required: true, message: 'Please select an AWS account' }]}
          >
            <Select placeholder="Select AWS account" disabled={!!editingLog || (editingLog && editingLog.isViewMode)}>
              {awsAccountsForDatabricks.map((acc) => (
                <Select.Option key={acc.accountId} value={`${acc.aws_account_name} (${acc.accountId})`}>
                  {acc.aws_account_name} ({acc.accountId})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="workspace"
            label="Workspace Selection"
            rules={[{ required: true, message: 'Please select a workspace' }]}
          >
            <Select
              placeholder="Select workspace"
              onChange={handleDatabricksWorkspaceChange}
              disabled={!!editingLog || (editingLog && editingLog.isViewMode)}
            >
              {databricksWorkspaces.map((ws) => (
                <Select.Option key={ws.workspace_id} value={ws.workspace_id}>
                  {ws.workspace_name || `Workspace ${ws.workspace_id}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {(!editingLog || !editingLog.isViewMode) && (
            <Form.Item
              name="patToken"
              label="PAT Token"
              rules={[{ required: true, message: 'Please enter PAT token' }]}
            >
              <Input.Password
                placeholder="Enter PAT token"
                visibilityToggle
              />
            </Form.Item>
          )}

          <Form.Item 
            label="Log Types"
            required
            rules={[
              {
                validator: (_, value) => {
                  const auditLogs = databricksForm.getFieldValue('auditLogs');
                  const realtimeLogs = databricksForm.getFieldValue('realtimeLogs');
                  if (!auditLogs && !realtimeLogs) {
                    return Promise.reject(new Error('Please select at least one log type'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Form.Item name="auditLogs" valuePropName="checked" noStyle>
              <Checkbox 
                disabled={editingLog && editingLog.isViewMode}
                onChange={(e) => {
                  if (!editingLog || !editingLog.isViewMode) {
                    if (e.target.checked) {
                      // Populate audit log path when checked
                      databricksForm.setFieldsValue({ 
                        auditLogsPath: 's3://databricks-logs-observability/audit' 
                      });
                    } else {
                      // Clear audit log path when unchecked
                      databricksForm.setFieldsValue({ auditLogsPath: undefined });
                    }
                  }
                }}
              >
                Audit Logs
              </Checkbox>
            </Form.Item>
            <div style={{ fontSize: '12px', color: isDark ? '#adbac7' : '#666', marginTop: '4px', marginLeft: '24px' }}>
              Account-level audit events and API calls.
            </div>

            <Form.Item 
              name="realtimeLogs" 
              valuePropName="checked" 
              noStyle 
              style={{ marginTop: '16px' }}
              dependencies={['workspace']}
            >
              <Checkbox 
                disabled={editingLog && editingLog.isViewMode}
                onChange={(e) => {
                  if (!editingLog || !editingLog.isViewMode) {
                    if (e.target.checked && databricksForm.getFieldValue('workspace')) {
                      handleDatabricksWorkspaceChange(databricksForm.getFieldValue('workspace'));
                    } else if (!e.target.checked) {
                      databricksForm.setFieldsValue({ clusterLogsPath: undefined });
                      setDatabricksClusterLogPaths([]);
                    }
                  }
                }}
              >
                Realtime Logs
              </Checkbox>
            </Form.Item>
            <div style={{ fontSize: '12px', color: isDark ? '#adbac7' : '#666', marginTop: '4px', marginLeft: '24px' }}>
              Live cluster and job execution logs.
            </div>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.auditLogs !== currentValues.auditLogs}>
            {({ getFieldValue }) => {
              const auditLogsEnabled = getFieldValue('auditLogs');
              if (!auditLogsEnabled) {
                return null;
              }
              return (
                <Form.Item
                  name="auditLogsPath"
                  label="Audit Logs Path"
                  dependencies={['auditLogs']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        if (getFieldValue('auditLogs')) {
                          if (!value) {
                            return Promise.reject(new Error('Please enter audit logs path'));
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.auditLogsPath !== currentValues.auditLogsPath}>
                    {({ getFieldValue }) => {
                      const currentValue = getFieldValue('auditLogsPath');
                      const isViewMode = editingLog && editingLog.isViewMode;
                      return (
                        <Input
                          placeholder="s3://databricks-logs-observability/audit"
                          disabled={isViewMode}
                          style={{ 
                            color: currentValue ? (isDark ? '#e6edf3' : '#000000') : undefined,
                            WebkitTextFillColor: currentValue ? (isDark ? '#e6edf3' : '#000000') : undefined
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.realtimeLogs !== currentValues.realtimeLogs}>
            {({ getFieldValue }) => {
              const realtimeLogsEnabled = getFieldValue('realtimeLogs');
              if (!realtimeLogsEnabled) {
                return null;
              }
              return (
                <Form.Item
                  name="clusterLogsPath"
                  label="Cluster Logs Path"
                  dependencies={['realtimeLogs', 'workspace']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        if (getFieldValue('realtimeLogs')) {
                          if (!value) {
                            return Promise.reject(new Error('Please select cluster log path'));
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Select 
                    placeholder="Select cluster log path" 
                    disabled={!databricksForm.getFieldValue('workspace') || (editingLog && editingLog.isViewMode)}
                    loading={databricksClusterLogPaths.length === 0 && databricksForm.getFieldValue('workspace')}
                    notFoundContent={
                      databricksForm.getFieldValue('workspace') && databricksClusterLogPaths.length === 0 
                        ? 'No cluster log paths available. Please check your workspace configuration.' 
                        : !databricksForm.getFieldValue('workspace')
                        ? 'Please select a workspace first'
                        : 'No data'
                    }
                  >
                    {databricksClusterLogPaths.map((path, index) => (
                      <Select.Option key={index} value={path}>
                        {path}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Source Details</Title>
            {selectedSourceDetail && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {selectedSourceDetail.accountType === 'databricks' 
                  ? selectedSourceDetail.workspaceName 
                  : selectedSourceDetail.source}
              </Text>
            )}
          </div>
        }
        placement="right"
        width={480}
        onClose={() => {
          setShowDetailDrawer(false);
          setSelectedSourceDetail(null);
        }}
        open={showDetailDrawer}
        extra={
          <Space>
            <Button
              icon={<EditFilled />}
              onClick={() => {
                setShowDetailDrawer(false);
                handleEditLogs(selectedSourceDetail);
              }}
              disabled={isViewer}
            >
              Edit
            </Button>
            <Button type="primary" onClick={() => setShowDetailDrawer(false)}>
              Close
            </Button>
          </Space>
        }
      >
        {selectedSourceDetail && (
          <div>
            {/* Source Info Card */}
            <Card 
              size="small"
              style={{ marginBottom: 20, borderRadius: 8 }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: selectedSourceDetail.source === 'CloudWatch' 
                    ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                    : 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedSourceDetail.source === 'CloudWatch' ? (
                    <CloudOutlined style={{ fontSize: 24, color: '#fff' }} />
                  ) : (
                    <DatabaseOutlined style={{ fontSize: 24, color: '#fff' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ margin: '0 0 4px 0', color: isDark ? '#e6edf3' : '#000' }}>
                    {selectedSourceDetail.accountType === 'databricks' 
                      ? selectedSourceDetail.workspaceName 
                      : selectedSourceDetail.source}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#adbac7' : undefined }}>
                    Account: {selectedSourceDetail.AccountId}
                  </Text>
                </div>
              </div>
              
              <Tag 
                color={selectedSourceDetail.enabled !== false ? 'success' : 'default'}
                style={{ fontSize: 13 }}
              >
                {selectedSourceDetail.enabled !== false ? 'Active' : 'Inactive'}
              </Tag>
            </Card>

            {/* Configuration */}
            <Card 
              title={<Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Configuration</Text>}
              size="small"
              style={{ marginBottom: 20, borderRadius: 8 }}
            >
              <Row gutter={[16, 12]}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#8b949e' : '#666666' }}>Account ID</Text>
                  <div style={{ fontSize: 14, fontWeight: 500, color: isDark ? '#e6edf3' : '#333333', marginTop: 4 }}>
                    {selectedSourceDetail.accountType === 'databricks' && selectedSourceDetail.accountName
                      ? selectedSourceDetail.accountName
                      : selectedSourceDetail.AccountId}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#8b949e' : '#666666' }}>Service</Text>
                  <div style={{ fontSize: 14, fontWeight: 500, color: isDark ? '#e6edf3' : '#333333', marginTop: 4 }}>
                    {selectedSourceDetail.source}
                  </div>
                </Col>
                {selectedSourceDetail.accountType === 'databricks' && (
                  <>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#8b949e' : '#666666' }}>Workspace</Text>
                      <div style={{ fontSize: 14, fontWeight: 500, color: isDark ? '#e6edf3' : '#333333', marginTop: 4 }}>
                        {selectedSourceDetail.workspaceName || 'N/A'}
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#8b949e' : '#666666' }}>AWS Account</Text>
                      <div style={{ fontSize: 14, fontWeight: 500, color: isDark ? '#e6edf3' : '#333333', marginTop: 4 }}>
                        {selectedSourceDetail.awsAccountId || 'N/A'}
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Card>

            {/* Log Entries */}
            <Card 
              title={
                <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Log Entries ({selectedSourceDetail.logsSelected?.length || 0})
                </Text>
              }
              size="small"
              style={{ marginBottom: 20, borderRadius: 8 }}
            >
              {selectedSourceDetail.logsSelected && selectedSourceDetail.logsSelected.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedSourceDetail.logsSelected.map((arn, idx) => {
                    const { log_group, bucket, path } = parseArn(arn);
                    
                    if (selectedSourceDetail.source === "s3" && bucket) {
                      return (
                        <div
                          key={idx}
                          style={{
                            padding: '12px',
                            borderRadius: 8,
                            background: isDark ? '#1c2128' : '#f0f8ff',
                            borderLeft: '4px solid #1890ff',
                            border: isDark ? '1px solid #30363d' : 'none'
                          }}
                        >
                          <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14, color: isDark ? '#e6edf3' : '#000' }}>
                            Bucket: {bucket}
                          </div>
                          {path && (
                            <div style={{ fontSize: 12, color: isDark ? '#adbac7' : '#666' }}>
                              Path: {path}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: '12px',
                          borderRadius: 8,
                          background: isDark ? '#1c2128' : '#f5f5f5',
                          borderLeft: '4px solid #52c41a',
                          border: isDark ? '1px solid #30363d' : 'none'
                        }}
                      >
                        <div style={{ fontWeight: 500, fontSize: 14, color: isDark ? '#e6edf3' : '#333333', wordBreak: 'break-all' }}>
                          {log_group !== "N/A" ? log_group : arn}
                        </div>
                        {arn !== log_group && (
                          <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#999', marginTop: 4 }}>
                            {arn}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty description="No log entries" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>

            {/* Action Buttons */}
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                block
                icon={<EyeOutlined />}
                onClick={() => {
                  setShowDetailDrawer(false);
                  // Use the same view modal for both AWS and Databricks - data is already in the record
                  setViewingLog(selectedSourceDetail);
                  setViewModalVisible(true);
                }}
              >
                View Logs
              </Button>
              <Button
                danger
                block
                icon={<DeleteFilled />}
                onClick={() => {
                  setShowDetailDrawer(false);
                  handleDeleteLogs(selectedSourceDetail);
                }}
                disabled={isViewer}
              >
                Remove Source
              </Button>
            </Space>
          </div>
        )}
      </Drawer>

      {/* Onboarding Wizard Modal */}
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Onboard Log Source</Title>
            <Steps
              current={wizardStep - 1}
              size="small"
              style={{ marginTop: 16 }}
              items={[
                { title: 'Account' },
                { title: 'Service' },
                { title: 'Logs' },
                { title: 'Review' }
              ]}
            />
          </div>
        }
        open={showOnboardingWizard}
        onCancel={() => {
          setShowOnboardingWizard(false);
          setWizardStep(1);
          resetModalState();
        }}
        width={700}
        footer={[
          <Button
            key="back"
            onClick={() => {
              if (wizardStep > 1) {
                setWizardStep(wizardStep - 1);
              } else {
                setShowOnboardingWizard(false);
                setWizardStep(1);
                resetModalState();
              }
            }}
          >
            {wizardStep === 1 ? 'Cancel' : 'Back'}
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={async () => {
              if (wizardStep < 4) {
                // Validate current step
                if (wizardStep === 1) {
                  try {
                    await form.validateFields(['accountId']);
                    setWizardStep(2);
                  } catch (e) {
                    // Validation failed
                  }
                } else if (wizardStep === 2) {
                  try {
                    await form.validateFields(['service']);
                    setWizardStep(3);
                  } catch (e) {
                    // Validation failed
                  }
                } else if (wizardStep === 3) {
                  if (logEntries.length > 0) {
                    setWizardStep(4);
                  } else {
                    message.warning('Please add at least one log entry');
                  }
                }
              } else {
                // Final step - save
                await handleSaveLogs();
                setShowOnboardingWizard(false);
                setWizardStep(1);
              }
            }}
          >
            {wizardStep === 4 ? 'Complete Setup' : 'Next'}
          </Button>
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="log_config_form_modal" requiredMark={false}>
          {/* Step 1: Account Selection */}
          {(wizardStep === 1) && (
            <div>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                Select the AWS account where you want to configure logs
              </Paragraph>
              <Form.Item
                name="accountId"
                label={<span>Account ID <span style={{ color: 'red' }}>*</span></span>}
                rules={[{ required: true, message: "Please select an account ID" }]}
              >
                <Select
                  placeholder="Select Account ID"
                  size="large"
                  options={accountOptions.filter(opt => opt.type === 'aws').map(opt => ({
                    value: opt.accountId,
                    label: opt.label
                  }))}
                  disabled={!!selectedAccount || selectedAccountType === 'databricks'}
                />
              </Form.Item>
            </div>
          )}

          {/* Step 2: Service Selection */}
          {(wizardStep === 2 || (wizardStep > 2 && form.getFieldValue("accountId"))) && (
            <div>
              {wizardStep >= 2 && (
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  Choose the log source type you want to configure
                </Paragraph>
              )}
              <Form.Item
                name="service"
                label={<span>Service <span style={{ color: 'red' }}>*</span></span>}
                rules={[{ required: true, message: "Please select a service" }]}
              >
                <Select
                  placeholder="Select Service"
                  size="large"
                  disabled={!!editingLog}
                  onChange={(value) => {
                    setCurrentArnInput("");
                    setEditingIndex(null);
                    if (!editingLog) {
                      setLogEntries([]);
                      if (wizardStep === 2) {
                        setWizardStep(3);
                      }
                    }
                    setIsAccountLocked(logEntries.length > 0 || !!editingLog);
                  }}
                  options={
                    selectedAccountType === 'databricks'
                      ? [{ label: "S3 Logs", value: "s3" }]
                      : [
                          { label: "S3 - Amazon Simple Storage Service", value: "s3" },
                          { label: "CloudWatch Logs", value: "CloudWatch" },
                        ]
                  }
                />
              </Form.Item>
            </div>
          )}

          {/* Step 3: Add Logs */}
          {(wizardStep === 3 || (wizardStep >= 3 && form.getFieldValue("service"))) && (
            <div>
              {form.getFieldValue("service") === "s3" ? (
                <>
                  {wizardStep >= 3 && (
                    <div style={{ marginBottom: 16 }}>
                      <Paragraph type="secondary">
                        Add S3 bucket paths to collect logs from. You can add multiple paths.
                      </Paragraph>
                      <Alert
                        message="S3 URI Format"
                        description="Enter S3 URIs in the format: s3://bucket-name/path/to/logs"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    </div>
                  )}
                  <Form.Item label={<span>S3 URI <span style={{ color: 'red' }}>*</span></span>} >
                    <Input
                      placeholder="e.g., s3://my-bucket/logs/"
                      size="large"
                      value={currentArnInput}
                      onChange={(e) => setCurrentArnInput(e.target.value)}
                      onPressEnter={handleAddLogEntry}
                    />
                  </Form.Item>
                  <Button
                    type="dashed"
                    onClick={handleAddLogEntry}
                    icon={<PlusOutlined />}
                    style={{ width: "100%", marginBottom: 16 }}
                    disabled={!currentArnInput.trim()}
                    size="large"
                  >
                    {editingIndex !== null ? "Update Log Entry" : "+ Add Log Entry"}
                  </Button>
                  {logEntries.length > 0 && (
                    <Form.Item label={`Added Logs (${logEntries.length})`}>
                      <Card size="small" className="logs-wizard-entries-card">
                        <div style={{ 
                          display: "flex", 
                          flexWrap: "wrap", 
                          gap: "8px"
                        }}>
                          {logEntries.map((entry, index) => (
                            <Tag
                              key={entry.arn}
                              closable
                              onClose={(e) => {
                                e.preventDefault();
                                handleDeleteLogEntry(index);
                              }}
                              onClick={() => handleTagClick(entry, index)}
                              className="custom-log-tag"
                            >
                              {entry.arn}
                            </Tag>
                          ))}
                        </div>
                      </Card>
                    </Form.Item>
                  )}
                  {wizardStep >= 3 && logEntries.length === 0 && (
                    <Alert
                      message="No logs added yet"
                      description="Please add at least one S3 URI to continue"
                      type="warning"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </>
              ) : form.getFieldValue("service") && form.getFieldValue("service").toLowerCase() === "cloudwatch" ? (
                <>
                  {wizardStep >= 3 && (
                    <div style={{ marginBottom: 16 }}>
                      <Paragraph type="secondary">
                        Add CloudWatch log group names to collect logs from. You can add multiple log groups.
                      </Paragraph>
                      <Alert
                        message="Log Group Format"
                        description="Enter log group names (e.g., /aws/lambda/my-function) or full ARNs"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    </div>
                  )}
                  <Form.Item
                    name="cloudwatchRegion"
                    label={<span>Region <span style={{ color: 'red' }}>*</span></span>}
                    rules={[{ required: true, message: "Please select the region for your log groups" }]}
                    initialValue="us-east-1"
                  >
                    <Select
                      placeholder="Select region"
                      size="large"
                      options={CLOUDWATCH_REGION_OPTIONS}
                      style={{ marginBottom: 16 }}
                    />
                  </Form.Item>
                  <Form.Item label={<span>Log Group Name or ARN <span style={{ color: 'red' }}>*</span></span>}>
                    <Input
                      placeholder="e.g., /aws/lambda/my-function"
                      size="large"
                      value={currentArnInput}
                      onChange={(e) => setCurrentArnInput(e.target.value)}
                      onPressEnter={handleAddLogEntry}
                    />
                  </Form.Item>
                  <Button
                    type="dashed"
                    onClick={handleAddLogEntry}
                    icon={<PlusOutlined />}
                    style={{ width: "100%", marginBottom: 16 }}
                    disabled={!currentArnInput.trim()}
                    size="large"
                  >
                    {editingIndex !== null ? "Update Log Entry" : "+ Add Log Entry"}
                  </Button>
                  {logEntries.length > 0 && (
                    <Form.Item label={`Added Logs (${logEntries.length})`}>
                      <Card size="small" className="logs-wizard-entries-card">
                        <div style={{ 
                          display: "flex", 
                          flexWrap: "wrap", 
                          gap: "8px"
                        }}>
                          {logEntries.map((entry, index) => (
                            <Tag
                              key={entry.arn}
                              closable
                              onClose={(e) => {
                                e.preventDefault();
                                handleDeleteLogEntry(index);
                              }}
                              onClick={() => handleTagClick(entry, index)}
                              className="custom-log-tag"
                            >
                              {entry.arn}
                            </Tag>
                          ))}
                        </div>
                      </Card>
                    </Form.Item>
                  )}
                  {wizardStep >= 3 && logEntries.length === 0 && (
                    <Alert
                      message="No logs added yet"
                      description="Please add at least one log group to continue"
                      type="warning"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Step 4: Review */}
          {wizardStep === 4 && (
            <div>
              <Title level={5} style={{ marginBottom: 16 }}>Review Configuration</Title>
              <Card>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Text strong>Account ID:</Text>
                    <div style={{ marginTop: 4, color: isDark ? '#e6edf3' : '#0f172a' }}>
                      {form.getFieldValue("accountId") || "Not selected"}
                    </div>
                  </Col>
                  <Col span={24}>
                    <Text strong>Service:</Text>
                    <div style={{ marginTop: 4, color: isDark ? '#e6edf3' : '#0f172a' }}>
                      {form.getFieldValue("service") || "Not selected"}
                    </div>
                  </Col>
                  <Col span={24}>
                    <Text strong>Log Entries ({logEntries.length}):</Text>
                    <div style={{ marginTop: 8 }}>
                      {logEntries.length > 0 ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {logEntries.map((entry, index) => (
                            <Tag key={index} style={{ marginBottom: 4 }}>
                              {entry.arn}
                            </Tag>
                          ))}
                        </Space>
                      ) : (
                        <Text type="secondary">No logs added</Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default LogsPage;
